import {
	clearFetchError,
	createMobileFiltersController,
	getPageFromLink,
	isMobileViewport,
	readUrlListingState,
	showFetchError,
	syncUrlParams,
} from '../utils/filterable-archive.js';

export class InTheNews {
	constructor() {
		document.querySelectorAll('.in-the-news[data-ajax="1"]').forEach((root) => {
			this.bind(root);
		});
	}

	bind(root) {
		const config = window.icarInTheNews;

		if (!config?.ajaxUrl) {
			return;
		}

		const results = root.querySelector('.in-the-news__results');

		if (!results) {
			return;
		}

		const instanceId = root.dataset.instance || '1';
		const defaultPerPage = root.dataset.defaultPerPage || '9';
		const activeFilters = root.querySelector('[data-in-the-news-active-filters]');
		let pendingFilterSnapshot = null;
		let skipRevertOnClose = false;

		const getSelectedLanguages = () =>
			[...root.querySelectorAll('.in-the-news__lang-input:checked')].map((input) => input.value);

		const getSelectedCoverages = () =>
			[...root.querySelectorAll('.in-the-news__coverage-input:checked')].map((input) => input.value);

		const snapshotFilters = () => ({
			s: root.querySelector('.in-the-news__search-input')?.value ?? '',
			languages: getSelectedLanguages(),
			coverages: getSelectedCoverages(),
		});

		const restoreFilterSnapshot = (snapshot) => {
			const searchInput = root.querySelector('.in-the-news__search-input');

			if (searchInput) {
				searchInput.value = snapshot.s;
			}

			root.querySelectorAll('.in-the-news__lang-input').forEach((input) => {
				input.checked = snapshot.languages.includes(input.value);
			});

			root.querySelectorAll('.in-the-news__coverage-input').forEach((input) => {
				input.checked = snapshot.coverages.includes(input.value);
			});
		};

		const { filtersBackdrop, setFiltersOpen } = createMobileFiltersController(root, {
			blockName: 'in-the-news',
			backdropSelector: '[data-in-the-news-filters-backdrop]',
			toggleSelector: '[data-in-the-news-open-filters]',
			panelSelector: '.in-the-news__filters-panel',
			closeSelector: '[data-in-the-news-close-filters]',
			onWillClose: () => {
				if (!skipRevertOnClose && pendingFilterSnapshot) {
					restoreFilterSnapshot(pendingFilterSnapshot);
				}

				pendingFilterSnapshot = null;
				skipRevertOnClose = false;
			},
		});

		const setFiltersOpenWithRevert = (open, { keepChanges = false } = {}) => {
			if (open && isMobileViewport()) {
				pendingFilterSnapshot = snapshotFilters();
			}

			if (!open) {
				skipRevertOnClose = keepChanges;
			}

			setFiltersOpen(open);
		};

		const updateActiveFilters = (html) => {
			if (!activeFilters || html === undefined) {
				return;
			}

			activeFilters.innerHTML = html;
		};

		const appendListToBody = (body, key, values) => {
			values.forEach((slug) => {
				body.append(`${key}[]`, slug);
			});
		};

		const applyStateToForm = (state) => {
			const searchInput = root.querySelector('.in-the-news__search-input');
			const perPageSelect = root.querySelector('.in-the-news__per-page-select');

			if (searchInput) {
				searchInput.value = state.s || '';
			}

			if (perPageSelect) {
				perPageSelect.value = state.posts_per_page || defaultPerPage;
			}

			const languages = state.news_lang || [];
			const coverages = state.news_coverage || [];

			root.querySelectorAll('.in-the-news__lang-input').forEach((input) => {
				input.checked = languages.includes(input.value);
			});

			root.querySelectorAll('.in-the-news__coverage-input').forEach((input) => {
				input.checked = coverages.includes(input.value);
			});
		};

		const fetchResults = async (params, { updateUrl = true, scroll = false, closeFilters = false } = {}) => {
			root.classList.add('is-loading');
			clearFetchError(results, 'in-the-news');

			const body = new FormData();
			body.append('action', config.action);
			body.append('nonce', config.nonce);
			body.append('default_per_page', defaultPerPage);
			body.append('instance_id', instanceId);

			const languages = params.news_lang || [];
			const coverages = params.news_coverage || [];

			Object.entries(params).forEach(([key, value]) => {
				if (
					key === 'news_lang' ||
					key === 'news_coverage' ||
					value === undefined ||
					value === null ||
					value === ''
				) {
					return;
				}

				body.append(key, String(value));
			});

			appendListToBody(body, 'news_lang', languages);
			appendListToBody(body, 'news_coverage', coverages);

			try {
				const response = await fetch(config.ajaxUrl, {
					method: 'POST',
					body,
					credentials: 'same-origin',
				});

				if (!response.ok) {
					throw new Error(`HTTP ${response.status}`);
				}

				const payload = await response.json();

				if (!payload?.success) {
					throw new Error('Request failed');
				}

				const main = results.querySelector('.in-the-news__main');

				if (main && payload.data.html) {
					main.outerHTML = payload.data.html;
				}

				if (payload.data.footerHtml) {
					const footer = root.querySelector('[data-in-the-news-footer]');

					if (footer) {
						footer.innerHTML = payload.data.footerHtml;
					}
				}

				if (payload.data.countHtml) {
					const count = root.querySelector('.in-the-news__sidebar .in-the-news__count');

					if (count) {
						count.outerHTML = payload.data.countHtml;
					}
				}

				updateActiveFilters(payload.data.activeFiltersHtml);

				if (updateUrl) {
					this.updateUrl(params, defaultPerPage);
				}

				if (scroll) {
					root.scrollIntoView({ behavior: 'smooth', block: 'start' });
				}

				if (closeFilters) {
					setFiltersOpenWithRevert(false, { keepChanges: true });
				}
			} catch {
				showFetchError(
					results,
					'in-the-news',
					'Unable to load results. Please try again.'
				);
			} finally {
				root.classList.remove('is-loading');
			}
		};

		const getCurrentState = (paged = '1') => {
			const searchInput = root.querySelector('.in-the-news__search-input');
			const perPageSelect = root.querySelector('.in-the-news__per-page-select');

			return {
				s: searchInput?.value?.trim() || '',
				news_lang: getSelectedLanguages(),
				news_coverage: getSelectedCoverages(),
				posts_per_page: perPageSelect?.value || defaultPerPage,
				paged: String(paged),
			};
		};

		const clearLanguageInputs = () => {
			root.querySelectorAll('.in-the-news__lang-input').forEach((input) => {
				input.checked = false;
			});
		};

		const clearCoverageInputs = () => {
			root.querySelectorAll('.in-the-news__coverage-input').forEach((input) => {
				input.checked = false;
			});
		};

		const uncheckLanguage = (slug) => {
			root.querySelectorAll(`.in-the-news__lang-input[value="${slug}"]`).forEach((input) => {
				input.checked = false;
			});
		};

		const uncheckCoverage = (slug) => {
			root.querySelectorAll(`.in-the-news__coverage-input[value="${slug}"]`).forEach((input) => {
				input.checked = false;
			});
		};

		const onPopState = () => {
			if (!root.isConnected) {
				return;
			}

			const state = readUrlListingState(defaultPerPage, {
				listParams: [
					{ param: 'news_lang', stateKey: 'news_lang' },
					{ param: 'news_coverage', stateKey: 'news_coverage' },
				],
			});
			applyStateToForm(state);
			fetchResults(state, { updateUrl: false });
		};

		window.addEventListener('popstate', onPopState);

		root.addEventListener('click', (event) => {
			const openButton = event.target.closest('[data-in-the-news-open-filters]');

			if (openButton && root.contains(openButton)) {
				event.preventDefault();
				setFiltersOpenWithRevert(true);
				return;
			}

			const closeButton = event.target.closest('[data-in-the-news-close-filters]');

			if (closeButton && root.contains(closeButton)) {
				event.preventDefault();
				setFiltersOpenWithRevert(false);
				return;
			}

			if (event.target === filtersBackdrop) {
				event.preventDefault();
				setFiltersOpenWithRevert(false);
				return;
			}

			const removeSearch = event.target.closest('[data-in-the-news-remove-search]');

			if (removeSearch && root.contains(removeSearch)) {
				event.preventDefault();

				const searchInput = root.querySelector('.in-the-news__search-input');

				if (searchInput) {
					searchInput.value = '';
				}

				const state = getCurrentState();
				state.s = '';

				fetchResults(state, { scroll: true });
				return;
			}

			const removeLang = event.target.closest('[data-in-the-news-remove-lang]');

			if (removeLang && root.contains(removeLang)) {
				event.preventDefault();

				const slug = removeLang.getAttribute('data-in-the-news-remove-lang');

				if (slug) {
					uncheckLanguage(slug);
				}

				fetchResults(getCurrentState(), { scroll: true });
				return;
			}

			const removeCoverage = event.target.closest('[data-in-the-news-remove-coverage]');

			if (removeCoverage && root.contains(removeCoverage)) {
				event.preventDefault();

				const slug = removeCoverage.getAttribute('data-in-the-news-remove-coverage');

				if (slug) {
					uncheckCoverage(slug);
				}

				fetchResults(getCurrentState(), { scroll: true });
				return;
			}

			const clearButton = event.target.closest('[data-in-the-news-clear]');

			if (clearButton && root.contains(clearButton)) {
				event.preventDefault();

				const searchInput = root.querySelector('.in-the-news__search-input');
				const perPageSelect = root.querySelector('.in-the-news__per-page-select');

				if (searchInput) {
					searchInput.value = '';
				}

				if (perPageSelect) {
					perPageSelect.value = defaultPerPage;
				}

				clearLanguageInputs();
				clearCoverageInputs();

				fetchResults(
					{
						s: '',
						news_lang: [],
						news_coverage: [],
						posts_per_page: defaultPerPage,
						paged: '1',
					},
					{ scroll: true, closeFilters: true }
				);
				return;
			}

			const copyButton = event.target.closest('.share-social__copy');

			if (copyButton && root.contains(copyButton)) {
				event.preventDefault();
				const link = copyButton.dataset.link;
				if (!link) {
					return;
				}
				navigator.clipboard.writeText(link);
				return;
			}

			const applyButton = event.target.closest('[data-in-the-news-apply-filters]');

			if (applyButton && root.contains(applyButton)) {
				event.preventDefault();
				fetchResults(getCurrentState(), { scroll: true, closeFilters: true });
				return;
			}

			const pageLink = event.target.closest('.in-the-news__pagination a');

			if (pageLink && root.contains(pageLink)) {
				event.preventDefault();

				const state = getCurrentState(getPageFromLink(pageLink));
				fetchResults(state, { scroll: true });
			}
		});

		const searchForm = root.querySelector('.in-the-news__search');
		const searchInput = root.querySelector('.in-the-news__search-input');

		if (searchForm) {
			searchForm.addEventListener('submit', (event) => {
				event.preventDefault();
				fetchResults(getCurrentState(), { scroll: true, closeFilters: true });
			});
		}

		if (searchInput) {
			let lastSearch = searchInput.value.trim();

			searchInput.addEventListener('input', () => {
				const value = searchInput.value.trim();

				if (value === '' && lastSearch !== '') {
					const state = getCurrentState();
					state.s = '';
					fetchResults(state);
				}

				lastSearch = value;
			});
		}

		root.addEventListener('change', (event) => {
			const filterInput = event.target.closest(
				'.in-the-news__lang-input, .in-the-news__coverage-input'
			);

			if (filterInput && root.contains(filterInput) && !isMobileViewport()) {
				fetchResults(getCurrentState(), { scroll: true });
				return;
			}

			const select = event.target.closest('.in-the-news__per-page-select');

			if (!select || !root.contains(select)) {
				return;
			}

			fetchResults(getCurrentState(), { scroll: true });
		});
	}

	updateUrl(params, defaultPerPage) {
		const url = new URL(window.location.href);

		syncUrlParams(url, params, defaultPerPage, ['news_lang', 'news_coverage'], (targetUrl, state) => {
			if (state.news_lang?.length) {
				targetUrl.searchParams.set('news_lang', state.news_lang.join(','));
			}

			if (state.news_coverage?.length) {
				targetUrl.searchParams.set('news_coverage', state.news_coverage.join(','));
			}
		});

		window.history.pushState({}, '', url);
	}
}
